import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "../Navbar/Navbar";
import Footer from "../Footer/Footer";

// ── Plans & Courses data ─────────────────────────────────────────────────────
const PLANS = [
  {
    id: "free",
    label: "Free Basic Member",
    icon: "🌐",
    accent: "#64748b",
    badge: null,
    desc: "Perfect for new users exploring PilotLinkUSA for the first time.",
    monthly: 0,
    yearly: 0,
    priceNote: "Always free — no credit card required to start.",
    banner: {
      title: "🎉 7-Day Marketplace Access Included",
      body:
        "New members get full access to the Marketplace for 7 days — browse loads, view driver listings, explore tools, and see how everything works before choosing a plan. After 7 days, Marketplace access continues only if you select a Driver or Company plan.",
    },
    features: [
      "Browse all course listings",
      "View the public load board",
      "Basic profile listing",
      "Access demo content",
      "Community access",
      "7-day Marketplace access (full access)",
      "No credit card required to start",
    ],
    locked: [
      "Verified badge",
      "State cheat sheets",
      "Apply to loads",
      "Company tools",
      "Driver tools",
      "Marketplace access after 7 days",
    ],
    cta: "Get Started Free",
    ctaStyle: "ghost",
  },
  {
    id: "driver",
    label: "Pro Driver",
    icon: "👤",
    accent: "#0891b2",
    badge: "Most Popular",
    highlight: true,
    desc: "Everything a professional pilot car operator needs to get hired and grow.",
    monthly: 29,
    yearly: 299,
    yearlySavings: 49,
    features: [
      "Verified Driver Badge on your profile",
      "Companies can find & contact you directly",
      "Unlock all State Cheat Sheets (full access)",
      "State compliance PDFs after passing training",
      "Exclusive member pricing on all PilotLinkUSA courses",
      "Apply to unlimited loads — no limits",
      "Invoice builder + send directly to companies",
      "Priority support for professional drivers",
    ],
    note: "Already certified? Upload your cert and get verified instantly. No course required.",
    footNote: "No contracts. Cancel anytime.",
    cta: "Become a Member",
    ctaStyle: "primary",
  },
  {
    id: "company",
    label: "Company Plan",
    icon: "🏢",
    accent: "#7c3aed",
    badge: "For Carriers",
    desc:
      "Hire certified drivers. Post loads. Manage your fleet from one dashboard. Built for carriers, fleet managers, and dispatchers alike.",
    monthly: 79,
    yearly: 799,
    yearlySavings: 149,
    perLoad: 15,
    features: [
      "Post loads instantly for only $15 per load",
      "Search our certified driver database",
      "View full driver profiles + credentials",
      "Verify driver certification status (API included)",
      "Receive & approve driver invoices in one place",
      "Export invoices to QuickBooks — ready for accounting",
      "Priority support for carriers & dispatch teams",
    ],
    footNote: "No contracts. Cancel anytime.",
    cta: "Register Company",
    ctaStyle: "primary",
  },
  {
    id: "dispatcher",
    label: "Dispatcher Plan",
    icon: "🎧",
    accent: "#16a34a",
    badge: "For Dispatch Teams",
    desc:
      "Built for dispatchers who need real-time visibility, faster coordination, and complete control over every load.",
    monthly: 59,
    yearly: 599,
    yearlySavings: 109,
    tagline: "Real-time control for every load.",
    features: [
      "Full Dispatcher Dashboard",
      "Live Order Board",
      "Real-Time GPS Tracking",
      "Route Progress & ETA Monitoring",
      "Driver Communication Tools",
      "Alerts & Notifications",
      "Document Storage",
      "Reports & Trip Logs",
      "1 Dispatcher Seat Included",
    ],
    addons: [
      "Advanced Alerts Package — +$15/mo",
      "Additional Dispatcher Seats — +$39/mo each",
      "Load Posting — $15 per load",
      "Driver Verification API — +$29/mo",
      "Fleet Management Tools — +$25/mo",
    ],
    footNote: "No contracts. Cancel anytime.",
    cta: "Set Up Dispatch",
    ctaStyle: "primary",
  },
];

const COURSES = [
  { name: "Pilot Car Starter Guide", price: 49, original: 79, accent: "#059669", tag: "Beginner", desc: "Start your pilot car career the right way. Learn the exact equipment you need, how permits work, first-job preparation, and CB communication protocols used by professional escorts.", badge: "Perfect for new drivers" },
  { name: "Single-State Certification", price: 129, original: 159, accent: "#0d9488", tag: "State-Specific", desc: "Get fully certified for one state without purchasing a multi-state bundle. Covers regulations, escort requirements, compliance rules, and exam prep for your chosen state.", badge: "Perfect for local operations" },
  { name: "NY Standalone Certification", price: 149, original: 179, accent: "#d97706", tag: "NY Only", desc: "New York has zero reciprocity and some of the strictest pilot car regulations in the country. This course prepares you to meet NY's unique standards and pass the exam.", badge: "NY Essential" },
  { name: "PEVO 14-State Certification", price: 249, original: 299, accent: "#0891b2", tag: "PEVO", desc: "Operate legally across 14 states with one certification. Breaks down the PEVO reciprocity system and everything required to run professional operations across the region.", badge: "Best Value" },
  { name: "PPCA Master Course (All 50 States)", price: 499, original: 599, accent: "#1e56a8", tag: "All 50 States", desc: "The ultimate nationwide training package. Covers every compliance group, every red-flag scenario, and full exam prep for all 50 states, plus multi-state operational guidance.", badge: "Most Popular" },
  { name: "Pilot Car Business Starter", price: 349, original: 449, accent: "#7c3aed", tag: "Business", desc: "Build a profitable pilot car business from the ground up. LLC setup, insurance requirements, pricing strategy, contract acquisition, and how to win company clients consistently.", badge: "Professional Upgrade" },
];

const FAQS = [
  { q: "Do I need a subscription to take courses?", a: "No. Courses are one-time payments — access is provided for the duration of your active certification period. Memberships are separate and unlock features like verified badges, cheat sheets, and load applications." },
  { q: "What's the pass guarantee?", a: "If you complete all modules and fail your exam, we reset your access so you can study again and retake. Free of charge. No questions asked." },
  { q: "Can I join as a member without taking a course?", a: "Yes. If you already have a PEVO, PPCA, or NY cert, just upload it to get your Verified Badge instantly. No course needed." },
  { q: "How does Company Plan billing work?", a: "Choose $79/month, $799/year (save $149), or pay per load at $15 each with no subscription. No contracts — cancel anytime." },
  { q: "What does the Dispatcher Plan include?", a: "A full dispatch command center — live order board, real-time GPS tracking, ETA monitoring, communication tools, alerts, and reporting — for $59/month or $599/year, with optional add-ons like extra seats and fleet management tools." },
  { q: "Are course certificates officially recognized?", a: "Our PPCA course prepares you for the official PPCA exam. The certificate of completion is our internal record — the official cert comes from the testing body." },
  { q: "Do I need a Pro Driver membership to get my certificate after passing a course?", a: "No — your certificate of completion is yours once you pass, regardless of membership. Pro Driver membership additionally unlocks a downloadable state compliance PDF and your Verified Badge, which companies use to confirm your status at a glance." },
  { q: "What's the difference between PEVO and PPCA?", a: "PEVO is a state-level reciprocity program (14 states). PPCA is a national organization. The PPCA course covers all 50 states and satisfies both PEVO requirements." },
];

// ── Sub-components ───────────────────────────────────────────────────────────
function PlanCard({ plan, tk, billing }) {
  const isGhost = plan.ctaStyle === "ghost";
  const isFree = plan.monthly === 0 && plan.yearly === 0;
  const showYearly = billing === "yearly" && !isFree;
  const displayPrice = showYearly ? plan.yearly : plan.monthly;
  const period = isFree ? "" : showYearly ? "/yr" : "/mo";

  return (
    <div
      style={{
        background: tk.panelBg,
        borderRadius: 20,
        padding: "28px 24px",
        display: "flex",
        flexDirection: "column",
        gap: 18,
        border: plan.highlight ? `2px solid ${plan.accent}` : `1px solid ${tk.border}`,
        boxShadow: plan.highlight
          ? `0 0 0 4px ${plan.accent}18, 0 12px 40px rgba(0,0,0,${tk.D ? 0.3 : 0.1})`
          : `0 2px 8px rgba(0,0,0,${tk.D ? 0.2 : 0.05})`,
        position: "relative",
        transform: plan.highlight ? "scale(1.02)" : "none",
      }}
    >
      {plan.badge && (
        <div
          style={{
            position: "absolute",
            top: -1,
            left: "50%",
            transform: "translateX(-50%)",
            background: plan.accent,
            color: "#fff",
            fontSize: 10,
            fontWeight: 800,
            padding: "5px 18px",
            borderRadius: "0 0 12px 12px",
            letterSpacing: ".08em",
            whiteSpace: "nowrap",
          }}
        >
          {plan.badge.toUpperCase()}
        </div>
      )}
      {plan.badge && <div style={{ height: 12 }} />}

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <div
          style={{
            width: 46,
            height: 46,
            borderRadius: 13,
            background: `${plan.accent}18`,
            border: `1.5px solid ${plan.accent}30`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 22,
            flexShrink: 0,
          }}
        >
          {plan.icon}
        </div>
        <div>
          <div style={{ fontSize: 18, fontWeight: 900, color: tk.text, fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
            {plan.label}
          </div>
          <div style={{ fontSize: 12, color: tk.textSub, marginTop: 2 }}>{plan.desc}</div>
        </div>
      </div>

      {plan.tagline && (
        <div style={{ fontSize: 12.5, fontWeight: 700, color: plan.accent, marginTop: -8 }}>
          📡 {plan.tagline}
        </div>
      )}

      {/* Price */}
      <div style={{ paddingBottom: 14, borderBottom: `1px solid ${tk.border}` }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
          <span style={{ fontSize: 40, fontWeight: 900, color: tk.text, fontFamily: "'Plus Jakarta Sans',sans-serif", lineHeight: 1 }}>
            ${displayPrice}
          </span>
          {period && <span style={{ fontSize: 14, color: tk.textSub }}>{period}</span>}
        </div>
        {isFree ? (
          <div style={{ fontSize: 12, color: "#22c55e", fontWeight: 700, marginTop: 4 }}>{plan.priceNote}</div>
        ) : showYearly ? (
          <div style={{ fontSize: 12, color: "#22c55e", fontWeight: 700, marginTop: 4 }}>
            Save ${plan.yearlySavings}/year — lock in your rate
          </div>
        ) : plan.perLoad ? (
          <div style={{ fontSize: 12, color: tk.textSub, marginTop: 4 }}>
            or ${plan.perLoad} per load — pay only when you post
          </div>
        ) : (
          <div style={{ fontSize: 12, color: tk.textSub, marginTop: 4 }}>Billed monthly · switch to yearly and save</div>
        )}
      </div>

      {plan.banner && (
        <div
          style={{
            background: `${plan.accent}10`,
            border: `1px solid ${plan.accent}25`,
            borderRadius: 12,
            padding: "12px 14px",
          }}
        >
          <div style={{ fontSize: 12.5, fontWeight: 800, color: tk.text, marginBottom: 4 }}>{plan.banner.title}</div>
          <div style={{ fontSize: 11.5, color: tk.textSub, lineHeight: 1.7 }}>{plan.banner.body}</div>
        </div>
      )}

      {/* Features */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 0 }}>
        {plan.features.map((f, i) => (
          <div
            key={i}
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 9,
              padding: "7px 0",
              borderBottom: i < plan.features.length - 1 ? `1px solid ${tk.border}` : "none",
            }}
          >
            <span style={{ color: plan.accent, fontWeight: 800, fontSize: 13, flexShrink: 0, marginTop: 1 }}>✓</span>
            <span style={{ fontSize: 13, color: tk.textMid, lineHeight: 1.5 }}>{f}</span>
          </div>
        ))}
        {plan.locked &&
          plan.locked.map((f, i) => (
            <div
              key={i}
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 9,
                padding: "7px 0",
                borderBottom: `1px solid ${tk.border}`,
                opacity: 0.45,
              }}
            >
              <span style={{ fontSize: 13, flexShrink: 0, marginTop: 1 }}>✕</span>
              <span style={{ fontSize: 13, color: tk.textSub, textDecoration: "line-through" }}>{f}</span>
            </div>
          ))}
      </div>

      {plan.addons && (
        <div
          style={{
            background: tk.midBg,
            borderRadius: 12,
            padding: "12px 14px",
            border: `1px solid ${tk.border}`,
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 800, color: tk.text, marginBottom: 8 }}>🔥 Optional Add-Ons</div>
          {plan.addons.map((a, i) => (
            <div key={i} style={{ fontSize: 11.5, color: tk.textSub, padding: "3px 0", lineHeight: 1.6 }}>
              • {a}
            </div>
          ))}
        </div>
      )}

      {plan.note && (
        <div
          style={{
            background: `${plan.accent}10`,
            borderRadius: 10,
            padding: "10px 13px",
            fontSize: 11,
            color: tk.textSub,
            lineHeight: 1.65,
            border: `1px solid ${plan.accent}20`,
          }}
        >
          💡 {plan.note}
        </div>
      )}

      {plan.footNote && (
        <div style={{ fontSize: 11, color: tk.textSoft, textAlign: "center", fontWeight: 600 }}>{plan.footNote}</div>
      )}

      <Link to={`/signup?plan=${plan.id}&billing=${billing}`} style={{ textDecoration: "none" }}>
        <button
          style={{
            width: "100%",
            padding: "14px",
            borderRadius: 12,
            cursor: "pointer",
            fontFamily: "inherit",
            border: isGhost ? `1.5px solid ${tk.border}` : "none",
            background: isGhost ? "transparent" : `linear-gradient(135deg,${plan.accent},${plan.accent}cc)`,
            color: isGhost ? tk.textMid : "#fff",
            fontWeight: 800,
            fontSize: 15,
            boxShadow: !isGhost ? `0 4px 16px ${plan.accent}35` : "none",
            transition: "all .15s",
          }}
          onMouseEnter={(e) => {
            if (!isGhost) {
              e.currentTarget.style.filter = "brightness(1.08)";
              e.currentTarget.style.transform = "translateY(-1px)";
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.filter = "none";
            e.currentTarget.style.transform = "none";
          }}
        >
          {plan.cta}
        </button>
      </Link>
    </div>
  );
}

function CourseRow({ course, tk, onEnroll }) {
  const savings = Math.round(((course.original - course.price) / course.original) * 100);
  return (
    <div
      style={{
        background: tk.panelBg,
        border: `1px solid ${tk.border}`,
        borderRadius: 14,
        padding: "clamp(14px,3vw,20px) clamp(14px,3vw,22px)",
        display: "flex",
        alignItems: "center",
        gap: 16,
        flexWrap: "wrap",
        boxShadow: `0 1px 4px rgba(0,0,0,${tk.D ? 0.2 : 0.05})`,
        transition: "all .15s",
        borderLeft: `4px solid ${course.accent}`,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = `0 6px 20px rgba(0,0,0,${tk.D ? 0.3 : 0.1})`;
        e.currentTarget.style.transform = "translateX(2px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = `0 1px 4px rgba(0,0,0,${tk.D ? 0.2 : 0.05})`;
        e.currentTarget.style.transform = "none";
      }}
    >
      <span
        style={{
          fontSize: 11,
          fontWeight: 800,
          color: course.accent,
          background: `${course.accent}15`,
          padding: "3px 10px",
          borderRadius: 99,
          border: `1px solid ${course.accent}25`,
          whiteSpace: "nowrap",
          flexShrink: 0,
        }}
      >
        {course.tag}
      </span>
      <div style={{ flex: 1, minWidth: 180 }}>
        <div style={{ fontWeight: 800, fontSize: 15, color: tk.text, marginBottom: 3 }}>{course.name}</div>
        <div style={{ fontSize: 12, color: tk.textSub, lineHeight: 1.6 }}>{course.desc}</div>
        <div style={{ fontSize: 11, fontWeight: 700, color: course.accent, marginTop: 4 }}>✓ {course.badge}</div>
      </div>
      <div style={{ flexShrink: 0, textAlign: "right" }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 6, justifyContent: "flex-end", marginBottom: 6 }}>
          <span style={{ fontSize: 22, fontWeight: 900, color: tk.text, fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
            ${course.price}
          </span>
          <span style={{ fontSize: 13, color: tk.textSoft, textDecoration: "line-through" }}>${course.original}</span>
          <span style={{ fontSize: 11, fontWeight: 800, color: "#22c55e" }}>-{savings}%</span>
        </div>
        <button
          style={{
            padding: "8px 20px",
            borderRadius: 99,
            border: "none",
            background: course.accent,
            color: "#fff",
            fontWeight: 800,
            fontSize: 13,
            cursor: "pointer",
            boxShadow: `0 3px 10px ${course.accent}35`,
            fontFamily: "inherit",
          }}
          onClick={() => onEnroll(course)}
        >
          Enroll →
        </button>
      </div>
    </div>
  );
}

function FaqItem({ q, a, tk }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ border: `1px solid ${tk.border}`, borderRadius: 13, overflow: "hidden", transition: "border-color .15s" }}>
      <button
        onClick={() => setOpen((p) => !p)}
        style={{
          width: "100%",
          padding: "16px 20px",
          background: tk.panelBg,
          border: "none",
          cursor: "pointer",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 12,
          textAlign: "left",
          fontFamily: "inherit",
        }}
      >
        <span style={{ fontSize: 14, fontWeight: 700, color: tk.text, lineHeight: 1.4 }}>{q}</span>
        <span style={{ fontSize: 20, color: tk.textSub, transform: open ? "rotate(180deg)" : "none", transition: "transform .2s", flexShrink: 0 }}>
          ▾
        </span>
      </button>
      {open && (
        <div style={{ padding: "0 20px 18px", borderTop: `1px solid ${tk.border}`, background: tk.midBg }}>
          <p style={{ fontSize: 14, color: tk.textMid, lineHeight: 1.8, margin: "14px 0 0" }}>{a}</p>
        </div>
      )}
    </div>
  );
}

// ── Main Page ────────────────────────────────────────────────────────────────
export default function PricingPage({ isDarkMode, toggleTheme }) {
  const [view, setView] = useState("membership");
  const [billing, setBilling] = useState("monthly");
  const navigate = useNavigate();
  const D = isDarkMode;

  // Design tokens — matches the PilotLinkUSA site palette (navy / amber / teal / purple)
  const tk = {
    D,
    bg: D ? "#0d1f3c" : "#f4f6f9",
    panelBg: D ? "#142a4d" : "#ffffff",
    midBg: D ? "#1a3358" : "#f0f4f8",
    border: D ? "rgba(255,255,255,0.10)" : "#e2e8f0",
    text: D ? "#f0f4f8" : "#0d1f3c",
    textMid: D ? "rgba(240,244,248,0.85)" : "#334155",
    textSub: D ? "rgba(240,244,248,0.60)" : "#64748b",
    textSoft: D ? "rgba(240,244,248,0.40)" : "#94a3b8",
    amber: "#f59e0b",
    amberL: "#fbbf24",
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleEnroll = (course) => {
    navigate(`/signup?course=${encodeURIComponent(course.name)}`);
  };

  return (
    <div style={{ minHeight: "100vh", background: tk.bg, fontFamily: "'Inter',sans-serif", transition: "background .3s" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&family=Plus+Jakarta+Sans:wght@700;800;900&display=swap');*{box-sizing:border-box;}`}</style>
      <Navbar isDarkMode={isDarkMode} toggleTheme={toggleTheme} />

      {/* ── HERO ── */}
      <div
        style={{
          background: "linear-gradient(135deg,#0d1f3c 0%,#1a3358 60%,#0f2d56 100%)",
          padding: "clamp(40px,6vw,72px) clamp(16px,4vw,40px) clamp(32px,4vw,48px)",
          borderBottom: "3px solid #f59e0b",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          aria-hidden
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage: "radial-gradient(rgba(255,255,255,0.05) 1px,transparent 1px)",
            backgroundSize: "22px 22px",
            pointerEvents: "none",
          }}
        />
        <div
          aria-hidden
          style={{
            position: "absolute",
            top: "-15%",
            right: "0%",
            width: "clamp(200px,30vw,400px)",
            height: "clamp(200px,30vw,400px)",
            borderRadius: "50%",
            background: "radial-gradient(circle,rgba(251,191,36,0.14) 0%,transparent 70%)",
            pointerEvents: "none",
          }}
        />
        <div style={{ maxWidth: 720, margin: "0 auto", textAlign: "center", position: "relative", zIndex: 1 }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: tk.amberL, letterSpacing: ".14em", marginBottom: 14, textTransform: "uppercase" }}>
            Simple, Transparent Pricing
          </div>
          <h1
            style={{
              fontFamily: "'Plus Jakarta Sans',sans-serif",
              fontSize: "clamp(28px,5vw,52px)",
              fontWeight: 900,
              color: "#fff",
              margin: "0 0 14px",
              letterSpacing: "-.025em",
              lineHeight: 1.1,
            }}
          >
            Start Free.
            <br />
            <span
              style={{
                background: `linear-gradient(90deg,${tk.amber},${tk.amberL})`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              Upgrade When You're Ready.
            </span>
          </h1>
          <p style={{ fontSize: "clamp(14px,2vw,16px)", color: "rgba(255,255,255,0.60)", maxWidth: 500, margin: "0 auto 28px", lineHeight: 1.8 }}>
            No hidden fees. Pay for courses once — memberships unlock platform features for drivers, companies, and dispatch teams.
          </p>

          {/* View Toggle */}
          <div style={{ display: "inline-flex", background: "rgba(255,255,255,0.08)", borderRadius: 13, padding: 4, gap: 4, border: "1px solid rgba(255,255,255,0.12)", marginBottom: 16 }}>
            {[
              ["membership", "💳 Memberships"],
              ["courses", "📚 Courses"],
            ].map(([id, label]) => (
              <button
                key={id}
                onClick={() => setView(id)}
                style={{
                  padding: "10px clamp(16px,3vw,28px)",
                  borderRadius: 10,
                  border: "none",
                  cursor: "pointer",
                  fontSize: "clamp(13px,2vw,15px)",
                  fontWeight: 700,
                  fontFamily: "inherit",
                  transition: "all .15s",
                  background: view === id ? `linear-gradient(135deg,${tk.amber},${tk.amberL})` : "transparent",
                  color: view === id ? "#0d1f3c" : "rgba(255,255,255,0.65)",
                  boxShadow: view === id ? `0 3px 12px rgba(245,158,11,0.40)` : "none",
                }}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Billing Toggle — only relevant to memberships */}
          {view === "membership" && (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: billing === "monthly" ? "#fff" : "rgba(255,255,255,0.45)" }}>Monthly</span>
              <button
                onClick={() => setBilling((b) => (b === "monthly" ? "yearly" : "monthly"))}
                aria-label="Toggle billing period"
                style={{
                  width: 46,
                  height: 26,
                  borderRadius: 99,
                  border: "1px solid rgba(255,255,255,0.25)",
                  background: "rgba(255,255,255,0.10)",
                  position: "relative",
                  cursor: "pointer",
                }}
              >
                <span
                  style={{
                    position: "absolute",
                    top: 2,
                    left: billing === "yearly" ? 22 : 2,
                    width: 20,
                    height: 20,
                    borderRadius: "50%",
                    background: `linear-gradient(135deg,${tk.amber},${tk.amberL})`,
                    transition: "left .18s",
                  }}
                />
              </button>
              <span style={{ fontSize: 13, fontWeight: 700, color: billing === "yearly" ? "#fff" : "rgba(255,255,255,0.45)" }}>
                Yearly <span style={{ color: tk.amberL }}>· save more</span>
              </span>
            </div>
          )}
        </div>
      </div>

      {/* ── CONTENT ── */}
      <div style={{ maxWidth: 1280, margin: "0 auto", padding: "clamp(28px,5vw,56px) clamp(14px,4vw,28px) 80px" }}>
        {/* MEMBERSHIP VIEW */}
        {view === "membership" && (
          <>
            <div style={{ textAlign: "center", marginBottom: 36 }}>
              <h2 style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: "clamp(22px,4vw,30px)", fontWeight: 900, color: tk.text, marginBottom: 8 }}>
                Choose Your Membership
              </h2>
              <p style={{ fontSize: 15, color: tk.textSub }}>All plans include access to the public load board and course catalog.</p>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit,minmax(min(100%,270px),1fr))",
                gap: 18,
                alignItems: "start",
                marginBottom: 36,
              }}
            >
              {PLANS.map((p) => (
                <PlanCard key={p.id} plan={p} tk={tk} billing={billing} />
              ))}
            </div>

            {/* Value strips */}
            <div
              style={{
                background: tk.panelBg,
                border: `1px solid ${tk.border}`,
                borderRadius: 18,
                padding: "clamp(18px,3vw,28px) clamp(16px,3vw,32px)",
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit,minmax(170px,1fr))",
                gap: 20,
              }}
            >
              {[
                ["🔒", "No commitment", "Join free. Upgrade anytime. Cancel anytime."],
                ["🏅", "Cert upload", "Already certified? Upload and verify instantly."],
                ["💳", "No setup fees", "Choose monthly, yearly, or pay-per-load billing."],
                ["📱", "All devices", "Web, mobile, and tablet fully supported."],
              ].map(([icon, title, sub]) => (
                <div key={title} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                  <span style={{ fontSize: 22, flexShrink: 0 }}>{icon}</span>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: tk.text, marginBottom: 3 }}>{title}</div>
                    <div style={{ fontSize: 12, color: tk.textSub, lineHeight: 1.65 }}>{sub}</div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* COURSES VIEW */}
        {view === "courses" && (
          <>
            <div style={{ marginBottom: 24 }}>
              <h2 style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: "clamp(22px,4vw,30px)", fontWeight: 900, color: tk.text, marginBottom: 8 }}>
                ⭐ One-Time Course Pricing
              </h2>
              <p style={{ fontSize: 15, color: tk.textSub }}>
                Pay once. Train with confidence. Every course includes our official Pass Guarantee — if you don't pass, we reset your course at no cost.
              </p>
            </div>

            {/* Trust strip */}
            <div
              style={{
                background: D ? "rgba(30,86,168,0.15)" : "#dbeafe",
                border: `1px solid ${D ? "rgba(30,86,168,0.30)" : "rgba(30,86,168,0.20)"}`,
                borderRadius: 12,
                padding: "13px 18px",
                marginBottom: 22,
                display: "flex",
                gap: 16,
                flexWrap: "wrap",
              }}
            >
              {["🛡️ Pass guarantee", "⚡ Instant access", "📱 Any device", "🔄 Full certification-period access"].map((item) => (
                <span key={item} style={{ fontSize: 13, fontWeight: 700, color: D ? "#60a5fa" : "#1e56a8" }}>
                  {item}
                </span>
              ))}
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {COURSES.map((c) => (
                <CourseRow key={c.name} course={c} tk={tk} onEnroll={handleEnroll} />
              ))}
            </div>
          </>
        )}

        {/* FAQ */}
        <div style={{ marginTop: 60 }}>
          <h2
            style={{
              fontFamily: "'Plus Jakarta Sans',sans-serif",
              fontSize: "clamp(22px,4vw,28px)",
              fontWeight: 900,
              color: tk.text,
              textAlign: "center",
              marginBottom: 6,
            }}
          >
            Frequently Asked Questions
          </h2>
          <p style={{ fontSize: 14, color: tk.textSub, textAlign: "center", marginBottom: 28 }}>
            Still have questions? Email <strong style={{ color: tk.amber }}>hello@pilotlinkusa.com</strong>
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 10, maxWidth: 760, margin: "0 auto" }}>
            {FAQS.map((f) => (
              <FaqItem key={f.q} q={f.q} a={f.a} tk={tk} />
            ))}
          </div>
        </div>

        {/* Bottom CTA */}
        <div
          style={{
            marginTop: 52,
            background: "linear-gradient(135deg,#0d1f3c,#1a3358)",
            borderRadius: 22,
            padding: "clamp(32px,5vw,52px) clamp(20px,4vw,48px)",
            textAlign: "center",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div
            aria-hidden
            style={{
              position: "absolute",
              inset: 0,
              backgroundImage: "radial-gradient(rgba(255,255,255,0.04) 1px,transparent 1px)",
              backgroundSize: "18px 18px",
              pointerEvents: "none",
            }}
          />
          <div style={{ position: "relative", zIndex: 1 }}>
            <h2 style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: "clamp(22px,4vw,30px)", fontWeight: 900, color: "#fff", marginBottom: 10 }}>
              Ready to Get Started?
            </h2>
            <p style={{ fontSize: "clamp(14px,2vw,15px)", color: "rgba(255,255,255,0.55)", marginBottom: 28, lineHeight: 1.8, maxWidth: 480, margin: "0 auto 28px" }}>
              Join certified pilot car operators, carriers, and dispatch teams who use PilotLink to find jobs, stay compliant, and grow their business.
            </p>
            <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
              <Link
                to="/signup"
                style={{
                  padding: "clamp(12px,2vw,15px) clamp(20px,4vw,36px)",
                  borderRadius: 99,
                  background: `linear-gradient(135deg,${tk.amber},${tk.amberL})`,
                  color: "#0d1f3c",
                  fontWeight: 800,
                  fontSize: "clamp(14px,2vw,16px)",
                  textDecoration: "none",
                  boxShadow: `0 4px 18px rgba(245,158,11,0.40)`,
                  whiteSpace: "nowrap",
                }}
              >
                Join Free Today →
              </Link>
              <Link
                to="/training"
                style={{
                  padding: "clamp(12px,2vw,15px) clamp(20px,4vw,36px)",
                  borderRadius: 99,
                  border: "1.5px solid rgba(255,255,255,0.25)",
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: "clamp(14px,2vw,16px)",
                  textDecoration: "none",
                  whiteSpace: "nowrap",
                }}
              >
                Browse Courses
              </Link>
            </div>
          </div>
        </div>
      </div>
      <Footer isDarkMode={isDarkMode} />
    </div>
  );
}
